import type { NextPage } from "next";
import { UnauthedLayout } from "@/layouts";
import { NextSeo } from "next-seo";
import { InputScreen, type InputScreenState } from "./__components__/input-screen";
import { useCallback, useEffect, useState } from "react";
import { ConfirmScreen } from "./__components__/confirm-screen";
import * as Scroll from "react-scroll";
import { useRouter } from "next/router";
import { getPlanPriceUuid } from "@/pages/signup/plan/[priceUuid]/__components__/input-screen/module";
import { event } from "@/modules/utils/google-tag-manager";
import { datadogLogs } from "@datadog/browser-logs";
import { InvalidPanel } from "@/components/fragments/error/invalid-panel";
import { useConfirmPageChange } from "@/hooks/useConfirmPageChange/useConfirmPageChange";
import { pagesPath } from "@/pathpida/generated/$path";
import { isKonamiSports } from "@/modules/utils/subscription-info";

type ScreenState = "input" | "confirm";

const SignupPlanPage: NextPage = () => {
  const router = useRouter();
  const [screenState, setScreenState] = useState<ScreenState>("input");
  const [inputScreenState, setInputScreenState] = useState<InputScreenState>();

  // biome-ignore lint/correctness/useExhaustiveDependencies :[AUTOMATICALLY-INSERTED] This hook specifies more dependencies than necessary: setScreenState
  const handleInputScreenSubmit = useCallback(
    (data: InputScreenState) => {
      setInputScreenState(data);
      setScreenState("confirm");
    },
    [setScreenState],
  );

  const handleConfirmScreenSubmit = useCallback(() => {
    if (inputScreenState?.planData.id !== undefined) {
      // Google Tag Manager
      event(`mypage-signup-plan-thankyou`, {
        planUuid: inputScreenState.planData.id,
        customerId: inputScreenState.customerSlot.id,
        promotionCode: inputScreenState.promotionCode,
        prefectureCode: inputScreenState.applicationFormValue.customerAddress.prefectureCode,
      });

      const url = pagesPath.signup.plan._priceUuid(inputScreenState.planData.id).thanks.$url();
      // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
      void router.push({
        pathname: url.pathname,
        query: {
          ...url.query,
          customerId: inputScreenState.customerSlot.id,
          promotioncode: inputScreenState.promotionCode,
        },
      });
    } else {
      datadogLogs.logger.error(
        `unreachable: page signup/plan/[priceUuid] on handleConfirmScreenSubmit: planData.id = undefined, customerId = ${
          inputScreenState?.customerSlot.id ?? "undefined"
        }`,
      );
    }
  }, [inputScreenState, router]);

  const handleBackToInputScreen = useCallback(() => {
    setScreenState("input");
  }, []);

  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
    if (!router.isReady) return;

    // Google Tag Manager
    event(`mypage-signup-plan-view`, { planUuid: getPlanPriceUuid(router) });
  }, [router]);

  useEffect(() => {
    Scroll.animateScroll.scrollToTop({ smooth: false, duration: 0 });

    // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
    if (!router.isReady) return;

    // Google Tag Manager
    event(`mypage-signup-plan-${screenState}`, { planUuid: getPlanPriceUuid(router) });
  }, [router, screenState]);

  useConfirmPageChange({});

  return (
    <>
      <NextSeo title="新規お申し込み" noindex />
      <UnauthedLayout bgColor="light.main">
        {screenState === "input" ? (
          <InputScreen
            onInputScreenSubmit={handleInputScreenSubmit}
            previousInputScreenState={inputScreenState}
            isStepwise={!inputScreenState}
            showChatBalloon
            isKonamiSports={isKonamiSports(getPlanPriceUuid(router))}
          />
        ) : inputScreenState !== undefined ? (
          <ConfirmScreen
            {...inputScreenState}
            onCompletedSubmit={handleConfirmScreenSubmit}
            onClickBackButton={handleBackToInputScreen}
            headerMessage="お申込み STEP：2 / 3"
            isKonamiSports={isKonamiSports(getPlanPriceUuid(router))}
          />
        ) : (
          <InvalidPanel />
        )}
      </UnauthedLayout>
    </>
  );
};

export default SignupPlanPage;
